import { IS_NATIVE } from '@/config/platform';
import i18next from 'i18next';

/// Prépare les substituts natifs de `window.api` et `window.store`.
///
/// Le stockage est le point délicat. `window.store` est **synchrone** et lu à
/// plus de cent endroits, parfois au moment même où un module se charge ; l'API
/// Preferences de Capacitor, elle, est asynchrone. Rendre `window.store`
/// asynchrone reviendrait à réécrire tous les appelants.
///
/// On fait donc ce que fait déjà le préchargement d'Electron : tout est lu une
/// fois au démarrage dans un cache en mémoire, les lectures y répondent
/// immédiatement, et les écritures partent en arrière-plan vers Preferences.
///
/// Pourquoi ne pas s'en tenir au `localStorage` de la WebView : le système
/// l'efface sous pression mémoire. L'utilisateur se retrouverait déconnecté
/// sans rien avoir fait, et sans comprendre pourquoi.
export async function setupNative(): Promise<void> {
  if (!IS_NATIVE) return;

  // WKWebView agrandit la page dès qu'on touche un champ écrit en moins de
  // 16 px, et ne la réduit jamais ensuite. Le pincement est déjà coupé par
  // Capacitor : figer l'échelle ne retire rien à l'accessibilité.
  const viewport = document.querySelector<HTMLMetaElement>(
    'meta[name="viewport"]',
  );
  if (viewport) viewport.content += ', maximum-scale=1';

  // Pendant de `is-electron` (main.tsx) : global.css s'en sert pour couper ce
  // qui trahit une page web sous le doigt — bulle de Safari, libellés
  // sélectionnés.
  document.documentElement.classList.add('is-native');

  const { Preferences } = await import('@capacitor/preferences');
  const { App } = await import('@capacitor/app');

  const cache = new Map<string, string | null>();
  const { keys } = await Preferences.keys();
  await Promise.all(
    keys.map(async (key) => {
      const { value } = await Preferences.get({ key });
      cache.set(key, value);
    }),
  );

  const listeners = new Set<(key: StoreKeys, value: string | null) => void>();

  window.store = {
    get<T>(key: StoreKeys) {
      return (cache.get(key) ?? null) as T | null;
    },
    set(key: StoreKeys, value: string | null) {
      if (value === null) {
        cache.delete(key);
        void Preferences.remove({ key });
      } else {
        cache.set(key, value);
        void Preferences.set({ key, value });
      }
      for (const listener of listeners) listener(key, value);
    },
    clear() {
      cache.clear();
      void Preferences.clear();
    },
    onChange(listener) {
      listeners.add(listener);
    },
  };

  // Lien sortant touché, ouvert une fois passés les gestionnaires du
  // composant : s'il l'a déjà ouvert lui-même (ExternalLink), on s'abstient —
  // le plugin refuserait une seconde fenêtre.
  let pendingLink: string | null = null;

  window.api = {
    getVersion() {
      return __APP_VERSION__;
    },
    // Les mises à jour passent par les magasins : il n'y a rien à proposer ici,
    // et Apple refuse une application qui prétend se mettre à jour elle-même.
    checkUpdate() {
      return null;
    },
    update() {},
    onUpdateDownloaded() {
      return () => {};
    },
    onUpdateProgress() {
      return () => {};
    },
    // Une application native ne se ferme ni ne se redimensionne depuis son
    // contenu ; ces trois-là n'existent que pour la fenêtre d'Electron.
    close() {},
    minimize() {},
    maximize() {},
    async openExternal(url: string) {
      pendingLink = null;
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
    },
  };

  // Sans cela, le bouton retour d'Android quitte l'application depuis
  // n'importe quel écran — y compris au milieu d'un formulaire.
  void App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
      return;
    }
    void App.exitApp();
  });

  // Capacitor confie à Safari toute navigation hors de son origine, `_blank`
  // compris : chaque lien sortant (CGU, documentation, actualités) faisait
  // quitter l'application. Il s'ouvre désormais dans le navigateur intégré,
  // par-dessus elle. En capture, pour voir aussi les liens dont un composant
  // arrête la propagation ; les liens du routeur, internes, ne sont pas visés.
  document.addEventListener(
    'click',
    (event) => {
      const link =
        event.target instanceof Element
          ? event.target.closest('a[href]')
          : null;
      if (
        !(link instanceof HTMLAnchorElement) ||
        !/^https?:$/.test(link.protocol) ||
        link.origin === window.location.origin
      )
        return;
      event.preventDefault();
      pendingLink = link.href;
      setTimeout(() => {
        if (pendingLink) window.api.openExternal(pendingLink);
      });
    },
    true,
  );

  // `i18n.ts` a lu la langue à son import, avant que ce stockage soit chargé :
  // sans ceci, le choix fait dans l'application serait perdu à chaque lancement.
  const lang = window.store.get('lang');
  if (lang) await i18next.changeLanguage(lang);
}
